import React from 'react'

import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptions } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

interface ReeferControlHistoryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
}

interface ReeferControlHistory {
  id: number
  assetId: number
  userName: string
  commandId: string
  success: boolean
  commandState: string
  dateReceivedWs: Date
  dateProcessedUmd: Date
  dateProcessedBy2wcs: Date
  commandDescriptionOriginal: string
  responseDescription: string
}

const DATE_FORMAT = 'dd-MMM-yyyy hh:mm a'

const getCommandDescription = (commandId: string, units: 'Imperial' | 'Metric', commandDescription?: string) => {
  if (!commandDescription) {
    return ''
  }

  const document = new DOMParser().parseFromString(commandDescription, 'text/xml')

  if (commandId === 'ZONE_SETPOINT') {
    if (units === 'Metric') {
      return document.querySelector('CommandDescriptionInCelcius')?.textContent ?? ''
    }
    if (units === 'Imperial') {
      return document.querySelector('CommandDescriptionInFarenheit')?.textContent ?? ''
    }
  }
  return commandDescription
}

const ReeferControlHistory: InsightDefinition<ReeferControlHistoryParams, ReeferControlHistory[]> = {
  active: true,
  slug: 'asset/reefer-control-history',
  category: 'asset',
  name: 'Reefer Control History',
  description: 'List detailed reefer control history',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptions.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
    ],

    isValid: (configuration: ReeferControlHistoryParams) => {
      if (configuration.companyId && configuration.assetId) {
        return true
      }
      return 'Company and Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferControlHistoryParams) => {
    const { data } = await api.get<Asset[]>({
      path: `/fresh/assets?companyId=${parameters.companyId}&id=${parameters.assetId}&deleted=false`,
      origin: 'ReeferControlHistory#load',
    })
    const make = data?.[0]?.fridgeType?.make
    const path = `/insights/asset/reefer-control-history?${buildQuery({ ...parameters, make })}`
    const response = await api.get<ReeferControlHistory[]>({
      path,
      origin: 'ReeferControlHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferControlHistoryParams, title: string) => {
    const { data } = await api.get<Asset[]>({
      path: `/fresh/assets?companyId=${parameters.companyId}&id=${parameters.assetId}&deleted=false`,
      origin: 'ReeferControlHistory#load',
    })
    const make = data?.[0]?.fridgeType?.make
    const path = '/insights/asset/reefer-control-history'
    await downloadCsv(api, path, { ...parameters, make }, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={16} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No history found".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const body = data.slice().map((row, _) => ({
      Description: getCommandDescription(row.commandId, units, row.commandDescriptionOriginal),
      User: row.userName,
      Sent: row.dateReceivedWs
        ? {
            id: `sent-${row.id}`,
            date: row.dateReceivedWs,
            display: DateTime.fromJSDate(row.dateReceivedWs).toFormat(DATE_FORMAT),
          }
        : '',
      Processed: row.dateProcessedUmd
        ? {
            id: `processed-${row.id}`,
            date: row.dateProcessedUmd,
            display: DateTime.fromJSDate(row.dateProcessedUmd).toFormat(DATE_FORMAT),
          }
        : '',
      State: row.commandState,
      Reason: row.responseDescription,
    }))

    const dataItem = body[0]
    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(10)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as ReeferControlHistoryParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/fresh/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false`,
            origin: 'ReeferControlHistory#caption',
          })
          if (response.data?.length) {
            const asset = response.data[0]
            setAsset(asset)
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferControlHistory
